import React, {useState} from 'react'

// Third party libraries
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useShopperContext, useUsid} from '@salesforce/commerce-sdk-react'

// Custom Components
import CountrySelectorModalContent from '../country-selector-modal-content'

// Custom Hooks
import useShipToCountry from '../../hooks/use-ship-to-country'
import useMultiSite from '../../hooks/use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'

// Utils
import {sortShipToCountries, getRedirectLanguage} from '@app/utils/ship-to-country-utils'
import {setLocalStorageJSONItem} from '../../utils/utils'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

import {TURKEY_PARTNER_SITE, RUSSIA_PARTNER_SITE, HOME_HREF} from '@app/constants'
import {CART_HREF} from '../../constants'

/**
 * CountrySelectorModalCheckout is a React functional component that provides a modal interface
 * for selecting a shipping country during the checkout process. It manages the state and behavior
 * related to country selection and updates the shopper's context accordingly.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isOpen - A boolean indicating whether the modal is open.
 * @param {function} props.onClose - A callback function to be invoked when the modal is closed.
 *
 * @returns {JSX.Element} A JSX element representing the country selector modal.
 *
 * @example
 * <CountrySelectorModalCheckout isOpen={true} onClose={handleClose} />
 *
 * @description
 * This component handles the following functionalities:
 * - Displays a modal with a dropdown for selecting a country.
 * - Manages the state of the selected country code.
 * - Provides a confirmation mechanism to update the shipping country.
 * - Utilizes various hooks to interact with the shopper's context, site type, and navigation.
 * - Displays toast notifications for success and error scenarios.
 *
 * The component leverages several custom hooks:
 * - `useToast`: For displaying notifications.
 * - `useMultiSite`: To access locale information.
 * - `useShipToCountry`: To manage the shipping country state.
 * - `useShopperContextsMutation`: To update the shopper's context.
 * - `useSiteType`: To determine the site type (e.g., US or non-US).
 * - `useUsid`: To retrieve the unique session identifier.
 * - `useNavigation`: For navigation purposes.
 */
const CountrySelectorModalCheckout = ({isOpen, onClose}) => {
    const [selectedCountryCode, setSelectedCountryCode] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const {shipToCountry} = useShipToCountry()
    const intl = useIntl()
    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {enabled: !isServer}
    )

    const {site, locale, buildUrl} = useMultiSite()
    const {triggerPopinChangeShipToEvent} = useGTM()

    /**
     * Handles the change event for the country selection dropdown.
     *
     * This function is triggered when the user selects a different country from the dropdown menu.
     * It updates the state with the newly selected country's code.
     *
     * @param {Object} event - The event object associated with the change event.
     * @param {Object} event.target - The target element of the event.
     * @param {string} event.target.value - The value of the selected country code.
     */
    const handleCountryChange = (event) => {
        setSelectedCountryCode(event.target.value)
    }

    /**
     * Handles the confirmation action when a user selects a country in the checkout modal.
     *
     * This function is typically triggered when the user confirms their country selection,
     * and is responsible for processing the selected country, updating the relevant state,
     * and performing any necessary side effects such as closing the modal or triggering
     * further validation or API calls.
     *
     * @function handleConfirm
     * @returns {void}
     */
    const handleConfirm = async () => {
        let currentShipToCountry = shopperContext?.geoLocation?.countryCode
        setIsLoading(true)
        // Handle redirection cases
        const preferredLocale = getRedirectLanguage(selectedCountryCode, locale?.id)

        if (selectedCountryCode === 'US' && site.alias === 'eu') {
            // GTM Integration
            triggerPopinChangeShipToEvent(currentShipToCountry, selectedCountryCode)

            window.location.href = buildUrl(
                HOME_HREF + `?country=${selectedCountryCode}`,
                'us',
                preferredLocale
            )
        } else if (
            selectedCountryCode !== 'US' &&
            selectedCountryCode !== 'TR' &&
            selectedCountryCode !== 'RU' &&
            site.alias === 'us'
        ) {
            // GTM Integration
            triggerPopinChangeShipToEvent(currentShipToCountry, selectedCountryCode)

            window.location.href = buildUrl(
                HOME_HREF + `?country=${selectedCountryCode}`,
                'eu',
                preferredLocale
            )
        } else if (selectedCountryCode === 'TR') {
            // GTM Integration
            triggerPopinChangeShipToEvent(currentShipToCountry, selectedCountryCode)

            window.location.href = TURKEY_PARTNER_SITE
        } else if (selectedCountryCode === 'RU') {
            // GTM Integration
            triggerPopinChangeShipToEvent(currentShipToCountry, selectedCountryCode)

            window.location.href = RUSSIA_PARTNER_SITE
        } else {
            setLocalStorageJSONItem('countryCode', selectedCountryCode)
            onClose()

            // GTM Integration
            triggerPopinChangeShipToEvent(currentShipToCountry, selectedCountryCode)
            window.location.href = buildUrl(CART_HREF)
        }
        setIsLoading(false)
    }

    /**
     * Handles the closure of the country selector modal by performing several operations:
     * 1. Retrieves the shipping country based on the geolocation country code.
     * 2. Stores the country code in local storage.
     * 3. Updates the state with the selected shipping country.
     * 4. Sets the shopper context to the selected country.
     * 5. Invokes the onClose callback to close the modal.
     *
     * @async
     * @function handleClose
     * @returns {Promise<void>} A promise that resolves when all operations are complete.
     */
    const handleClose = async () => {
        onClose()
    }

    // Concatenate and sort countries
    const countries = sortShipToCountries(intl)

    return (
        <CountrySelectorModalContent
            isOpen={isOpen}
            onClose={handleClose}
            geolocCountry={shipToCountry?.id}
            geolocCountryCode={shipToCountry?.id}
            countries={countries}
            handleCountryChange={handleCountryChange}
            handleConfirm={handleConfirm}
            isLoading={isLoading}
        />
    )
}

CountrySelectorModalCheckout.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}

export default CountrySelectorModalCheckout
